import { useEffect, useState } from "react";
import {
  CheckCircle2,
  CreditCard,
  FileText,
  Handshake,
  Mail,
  RefreshCw,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { api } from "@/api/client";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";

const TIER_COLORS = {
  funding_ready: "var(--ds-color-success)",
  almost_ready: "var(--ds-color-accent)",
  improvement_recommended: "var(--ds-color-warning)",
  repair_required: "var(--ds-color-danger)",
};

const TIER_LABELS = {
  funding_ready: "Funding Ready",
  almost_ready: "Almost Ready",
  improvement_recommended: "Improvement",
  repair_required: "Repair Required",
};

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/analytics/overview");
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return (
      <AdminLayout title="Overview" subtitle="Loading…">
        <div data-testid="admin-overview-loading" className="text-[var(--ds-color-ink-muted)] text-sm">Loading analytics…</div>
      </AdminLayout>
    );
  }
  if (error) {
    return (
      <AdminLayout title="Overview">
        <div data-testid="admin-overview-error" className="text-sm" style={{ color: "var(--ds-color-danger)" }}>{error}</div>
      </AdminLayout>
    );
  }
  if (!data) return null;

  const aiSuccessPct = data.ai_usage.total_calls
    ? Math.round((data.ai_usage.successful / data.ai_usage.total_calls) * 100)
    : 0;
  const tierChartData = Object.entries(data.readiness.tier_distribution).map(([k, v]) => ({
    name: TIER_LABELS[k] || k,
    value: v,
    color: TIER_COLORS[k] || "var(--ds-color-ink-muted)",
  }));

  return (
    <AdminLayout
      title="Overview"
      subtitle="Operations snapshot — counts refresh on each load (cached 60s)."
      actions={
        <button
          type="button"
          onClick={load}
          data-testid="admin-overview-refresh"
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface)]"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      }
    >
      <AdminPageHelp
        title="Overview"
        purpose="At-a-glance operational KPIs for the platform — users, assessments, credit reports, dispute letters, AI usage, and readiness-tier distribution."
        configures="Nothing on this page changes live data. The Refresh button re-fetches the KPI aggregation (server-cached ~60s)."
        impact="Not applicable — read-only reporting."
        safety="Yes — read-only. Nothing here is destructive."
      />
      {/* KPI row */}
      <div data-testid="admin-overview-kpis" className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-7">
        <Kpi testId="kpi-total-users" icon={Users} label="Total users" value={data.users.total} sub={`+${data.users.new_this_week} this week`} />
        <Kpi testId="kpi-new-week" icon={TrendingUp} label="New this week" value={data.users.new_this_week} sub={`${data.users.new_today} today`} />
        <Kpi testId="kpi-assessments" icon={CheckCircle2} label="Total assessments" value={data.readiness.total_assessments} sub={data.readiness.avg_score != null ? `Avg ${data.readiness.avg_score}` : "—"} />
        <Kpi testId="kpi-reports" icon={CreditCard} label="Reports uploaded" value={data.credit_reports.total_uploaded} sub={`${data.credit_reports.successful_extractions} ok / ${data.credit_reports.failed_extractions} failed`} />
        <Kpi testId="kpi-letters" icon={Mail} label="Letters generated" value={data.dispute_letters.total_generated} sub={`${data.dispute_letters.total_downloaded} downloaded`} />
        <Kpi testId="kpi-partners" icon={Handshake} label="Active partners" value={data.partners.active_count} sub={`${data.partners.total_matches_computed} matches computed`} />
        <Kpi testId="kpi-ai" icon={FileText} label="AI calls" value={data.ai_usage.total_calls} sub={`${aiSuccessPct}% success`} />
        <Kpi testId="kpi-clicks" icon={TrendingUp} label="Apply clicks" value={data.partners.total_apply_clicks} sub="Lifetime" />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-5 mb-7">
        <Card title="Tier distribution" testId="overview-tier-distribution">
          {tierChartData.every((d) => d.value === 0) ? (
            <div className="h-48 flex items-center justify-center text-xs text-[var(--ds-color-ink-muted)]">
              No readiness assessments yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={tierChartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  stroke="none"
                >
                  {tierChartData.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-admin-border)", borderRadius: "var(--ds-radius-input)", fontSize: 12, color: "var(--ds-color-ink)" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-[var(--ds-color-ink-muted)]">
            {tierChartData.map((d) => (
              <li key={d.name} className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: d.color }} />
                {d.name} <span className="ml-auto text-[var(--ds-color-ink-muted)]">{d.value}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Path split" testId="overview-path-split">
          <div className="space-y-3 py-3">
            <PathBar label="Startup" value={data.readiness.path_distribution.startup} max={Math.max(1, data.readiness.path_distribution.startup + data.readiness.path_distribution.business)} color="var(--ds-color-accent)" />
            <PathBar label="Business" value={data.readiness.path_distribution.business} max={Math.max(1, data.readiness.path_distribution.startup + data.readiness.path_distribution.business)} color="var(--ds-color-success)" />
          </div>
          <div className="mt-2 text-[11px] text-[var(--ds-color-ink-muted)]">
            Profile completion: {data.profiles.completion_rate_pct}% ({data.profiles.completed}/{data.users.total})
          </div>
        </Card>

        <Card title="AI usage by feature" testId="overview-ai-feature">
          <ul className="space-y-2 text-sm">
            {Object.entries(data.ai_usage.by_feature).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between border-b border-[var(--ds-color-admin-border)] pb-1.5">
                <span className="text-[var(--ds-color-ink-muted)] capitalize">{k.replace("_", " ")}</span>
                <span className="font-medium tabular-nums">{v}</span>
              </li>
            ))}
            <li className="pt-1 text-xs text-[var(--ds-color-ink-muted)]">
              Providers: openai {data.ai_usage.by_provider.openai} · gemini {data.ai_usage.by_provider.gemini}
            </li>
            <li className="flex items-center gap-2 text-xs text-[var(--ds-color-ink-muted)]">
              <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" style={{ color: "var(--ds-color-success)" }} /> {data.ai_usage.successful}</span>
              <span className="inline-flex items-center gap-1"><XCircle className="w-3 h-3" style={{ color: "var(--ds-color-danger)" }} /> {data.ai_usage.failed}</span>
            </li>
          </ul>
        </Card>
      </div>

      {/* Lists row */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Top 5 common blockers" testId="overview-common-blockers">
          {data.common_blockers.length === 0 ? (
            <div className="text-xs text-[var(--ds-color-ink-muted)] py-2">No blockers recorded yet.</div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {data.common_blockers.map((b) => (
                <li key={b.blocker} className="flex items-start justify-between gap-3 border-b border-[var(--ds-color-admin-border)] pb-1.5">
                  <span className="text-[var(--ds-color-ink-muted)] line-clamp-2">{b.blocker}</span>
                  <span className="text-[var(--ds-color-ink-muted)] tabular-nums whitespace-nowrap">{b.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Top clicked partners" testId="overview-top-partners">
          {data.partners.top_clicked.length === 0 ? (
            <div className="text-xs text-[var(--ds-color-ink-muted)] py-2">No partner clicks tracked yet.</div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {data.partners.top_clicked.map((p) => (
                <li key={p.partner_name} className="flex items-center justify-between border-b border-[var(--ds-color-admin-border)] pb-1.5">
                  <span className="text-[var(--ds-color-ink-muted)] truncate">{p.partner_name}</span>
                  <span className="text-[var(--ds-color-ink-muted)] tabular-nums">{p.clicks}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}

function Kpi({ testId, icon: Icon, label, value, sub }) {
  return (
    <div
      data-testid={testId}
      className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] p-4"
      style={{ background: "var(--ds-color-surface)" }}
    >
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
        <span>{label}</span>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="font-display text-[24px] text-[var(--ds-color-ink)] mt-1.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-[var(--ds-color-ink-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}

function Card({ title, testId, children }) {
  return (
    <div
      data-testid={testId}
      className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] p-5"
      style={{ background: "var(--ds-color-surface)" }}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] mb-2.5">{title}</div>
      {children}
    </div>
  );
}

function PathBar({ label, value, max, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-[var(--ds-color-ink-muted)] mb-1">
        <span>{label}</span>
        <span className="tabular-nums text-[var(--ds-color-ink-muted)]">{value}</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--ds-color-admin-surface-muted)" }}>
        <div className="h-full" style={{ width: `${pct}%`, background: color, transition: "width 400ms" }} />
      </div>
    </div>
  );
}
